import { Injectable, Injector } from '@angular/core';
import { AuthenticationService } from '@app/auth/services/authentication.service';
import { InitializerService } from '@app/core/services/initializer.service';
import { AppInfo } from '@app/portal/models/app-info';
import { Branding } from '@app/portal/models/branding';
import { PortalDataService } from '@app/portal/services/portal-data.service';
import { PortalWindowDocumentService } from '@app/shared/services/portal-window-document.service';
import { PortalTranslateService } from '@app/translate/services/portal-translate.service';
import { throwPortalError } from '@app/utils/error/portal-error';
import { loggerFor } from '@app/utils/logger';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, Observable, of, ReplaySubject } from 'rxjs';
import { catchError, map, mergeMap, skip, take, tap } from 'rxjs/operators';
import { environment } from '../environments/environment';
import { UserPortalPreferences } from './auth/models/user-portal-preferences';
import { OIDCService } from './auth/services/oidc.service';
import { AuthUser } from './core/models/auth-user';
import { CurrentRouteData } from './routing/models/current-route-data';
import { RouterService } from './routing/router.service';
import { TranslationMeta } from './translate/models/translation-meta';
import { notNullish } from './utils/rxjs';

@Injectable({
  providedIn: 'root',
})
export class AppService {
  private logger = loggerFor('AppService');

  /**
   * True when the portal has been initialized (meaning all necessary information has been loaded).
   * @private
   */
  private _initialized$ = new ReplaySubject<boolean>(1);

  get initialized$(): Observable<boolean> {
    return this._initialized$.asObservable();
  }

  get branding() {
    return this.portalDataService.branding;
  }

  get info() {
    return this.portalDataService.appInfo;
  }

  get version() {
    return this.portalDataService.version;
  }

  constructor(
    private authService: AuthenticationService,
    private injector: Injector,
    private portalDataService: PortalDataService,
    private portalTranslateService: PortalTranslateService,
    private portalWindowDocumentService: PortalWindowDocumentService,
    private translateService: TranslateService,
    private routerService: RouterService,
    private oidcService: OIDCService
  ) {
    // App info must be reevaluated when we navigate to/from enterprise.
    // The reason is that the enterprise can be on different C5 node in cluster, having different C5 Release.
    // That is relevant for C5 Version check.
    // Note: this is a specific implementation case that is not supported by `CachedHelperService`.
    let enterpriseIdForInfo: string | null = null;
    this.routerService.currentRouteData$.subscribe((currentRouteData: CurrentRouteData) => {
      const enterpriseId = currentRouteData.params['enterpriseId'];
      if (enterpriseId !== enterpriseIdForInfo) {
        this.portalDataService.updateAppInfo(enterpriseId);
        enterpriseIdForInfo = enterpriseId;
      }
    });

    this.authService.isAuthenticated$.pipe(skip(1)).subscribe((isAuthenticated) => {
      if (isAuthenticated === false) {
        this.translateService.use(environment.defaultLanguage);
      } else {
        this.portalDataService.appInfo.reload();
      }
    });
  }

  initialize() {
    this.logger.info('Starting initialization');

    const appInfo$ = this.portalDataService.appInfo.dataWithError$();
    const authUser$ = this.authService.isAuthenticated$.pipe(
      mergeMap((isAuthenticated) => {
        if (isAuthenticated) {
          return this.authService.authUser$;
        }
        return of(null);
      })
    );
    const branding$ = this.portalDataService.branding.dataWithError$();
    const routeData$ = this.routerService.currentRouteData$.pipe(notNullish());
    const translationMeta$ = this.portalDataService.translationMeta.dataWithError$();
    const version$ = this.portalDataService.version.dataWithError$();

    return combineLatest([appInfo$, authUser$, branding$, translationMeta$, version$, routeData$]).pipe(
      take(1),
      mergeMap(([appInfo, authUser, branding, translationMeta]) => {
        const oidcSettings = appInfo.oidcSettings;
        this.portalTranslateService.setBranding(branding);
        return this.oidcService.initAndCheckSSO(oidcSettings, authUser).pipe(
          map((au: AuthUser | null) => {
            return [appInfo, au, branding, translationMeta];
          })
        );
      }),
      mergeMap(
        ([, authUser, branding, translationMeta]: [AppInfo, AuthUser | null, Branding, TranslationMeta]) => {
          let initialized$: Observable<any>;
          this.portalWindowDocumentService.setBaseTitle(branding.pageTitle);

          if (authUser !== null) {
            initialized$ = this.authService.authUserPortalPreferences.dataWithError$().pipe(
              take(1),
              tap((preferences: UserPortalPreferences) => {
                this.translateService.use(preferences.preferredLanguage);
              })
            );
          } else {
            // TODO maybe consider to check user browser settings for the language to use, instead of using some predefined lang directly.
            this.translateService.use(translationMeta.defaultLanguage);
            initialized$ = of(true);
          }

          /*
           * We cannot use constructor injection because it is possible that one of the services we
           * want to initialize needs the AppService, and that would create a circular dependency.
           */
          this.injector.get(InitializerService).initialize();

          return initialized$.pipe(
            tap((x) => {
              this.logger.log('Application is initialized:', x);
              this._initialized$.next(true);
              this._initialized$.complete();

              // we need to listen on changes of auth-user portal preferences and update the language
              this.authService.authUserPortalPreferences.data$.pipe(notNullish()).subscribe((prefs) => {
                const currentLang = this.translateService.currentLang;
                if (prefs.preferredLanguage !== currentLang) {
                  this.translateService.use(prefs.preferredLanguage);
                }
              });
            })
          );
        }
      ),
      catchError((err) => {
        this.logger.error('Initialization error', err);
        return throwPortalError({
          cause: err,
          id: 'portal initialization failed',
          i18n: 'general.err.maintenance',
        });
      })
    );
  }
}
